import { useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { Box } from '@mui/material';
import { useFetchMe } from '@retail/users/data-access';
import { DashboardPeriod, WelcomeCard } from '@retail/dashboard/components';
import { DashboardActions } from './blocks/DashboardActions';
import { KeyFiguresBlock } from './blocks/KeyFiguresBlock';
import { LatestChangesBlock } from './blocks/LatestChangesBlock';
import { CompetitorPricesBlock } from './blocks/CompetitorPricesBlock';

const DASHBOARD_PERIODS: DashboardPeriod[] = ['week', 'month', 'threeMonths'];
const DEFAULT_PERIOD: DashboardPeriod = 'month';

const usePeriodParam = (param: string): [DashboardPeriod, (next: DashboardPeriod) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const paramValue = searchParams.get(param) as DashboardPeriod | null;
  const period = paramValue && DASHBOARD_PERIODS.includes(paramValue) ? paramValue : DEFAULT_PERIOD;

  const setPeriod = useCallback(
    (next: DashboardPeriod) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(param, next);
          return params;
        },
        { replace: true }
      );
    },
    [param, setSearchParams]
  );

  return [period, setPeriod];
};

export const DashboardContainer = () => {
  const { data: user } = useFetchMe({ refetchOnWindowFocus: false });

  const [keyFiguresPeriod, setKeyFiguresPeriod] = usePeriodParam('period');
  const [competitorPeriod, setCompetitorPeriod] = usePeriodParam('competitorPeriod');

  return (
    <Box sx={{ height: '100%', width: '100%', overflowY: 'auto' }}>
      <Box
        sx={{
          maxWidth: ({ breakpoints }) => breakpoints.values.lg,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: 2,
        }}
      >
        <Box sx={{ mb: 4 }}>
          <WelcomeCard userName={user?.givenName ?? ''} />
        </Box>

        <Box
          sx={{
            display: 'grid',
            gap: 4,
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              lg: 'repeat(12, 1fr)',
            },
            gridAutoRows: 'min-content',
          }}
        >
          <Box sx={{ gridColumn: '1 / -1' }}>
            <DashboardActions />
          </Box>

          <Box sx={{ gridColumn: '1 / -1' }}>
            <KeyFiguresBlock
              period={keyFiguresPeriod}
              onPeriodChange={setKeyFiguresPeriod}
              periods={DASHBOARD_PERIODS}
            />
          </Box>

          <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1', lg: 'span 5' } }}>
            <LatestChangesBlock />
          </Box>

          <Box sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1', lg: 'span 7' } }}>
            <CompetitorPricesBlock
              period={competitorPeriod}
              onPeriodChange={setCompetitorPeriod}
              periods={DASHBOARD_PERIODS}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
